/**
 * Fix artifact helpers (Gap 2)
 *
 * - resolveFixType(): upgrades the MCP's coarse fixType into a richer, card-specific type by
 *   scanning the issue text. This lets Ruby route card-specific deliverables (disavow files,
 *   local/GBP fixes, CSV recommendations, robots/llms/canonical/redirect, review schema) that the
 *   MCP typically buckets as "technical"/"content".
 * - resolveArtifactFile(): maps a fix type + code language to a downloadable filename + MIME type,
 *   and flags file-type artifacts that should get an emphasized Download affordance.
 *
 * Note: the robust long-term source for the fix type is Authority44's per-issue `remediationHint`
 * surfaced through the MCP; until then, this keyword resolver is a client-side upgrade over the
 * single coarse bucket.
 */

import type { IssueFixType } from "./cmsGuides";

export interface IssueLike {
  fixType?: string;
  title?: string;
  description?: string;
  remediationHint?: string;
}

/** A44 remediationHint slug → IssueFixType. */
const HINT_TO_FIX_TYPE: Record<string, IssueFixType> = {
  "organization-schema": "entity",
  "person-schema": "entity",
  "structured-data": "schema",
  "faq-schema": "faq",
  "meta-description": "meta",
  "title-tag": "title",
  "heading-structure": "h1",
  "canonical": "canonical",
  "duplicate-content": "canonical",
  "redirect": "redirect",
  "broken-link": "redirect",
  "404": "redirect",
  "robots-txt": "robots",
  "llms-txt": "llms",
  "sitemap": "sitemap",
  "image-alt": "alt",
  "toxic-links": "disavow",
  "disavow": "disavow",
  "gbp-completeness": "local",
  "nap-consistency": "local",
  "local-citations": "local",
  "localbusiness-schema": "local",
  "review-schema": "review",
  "aggregate-rating": "review",
  "bulk-recommendations": "csv",
  "on-page-csv": "csv",
  "content-brief": "brief",
  "topical-cluster": "brief",
  "thin-content": "content",
  "content-depth": "content",
  "competitor-gap": "competitor",
};

const ALL_FIX_TYPES: Set<string> = new Set<IssueFixType>([
  "schema", "faq", "meta", "title", "h1", "content", "technical", "entity", "competitor",
  "canonical", "redirect", "robots", "llms", "sitemap", "alt", "disavow", "local", "review",
  "csv", "brief",
]);

/**
 * Card-specific detectors. Ordered most-specific first. These capture the deliverables the MCP
 * does not have a distinct fixType for (it tends to emit "technical"/"content").
 */
const CARD_SPECIFIC_RULES: { type: IssueFixType; pattern: RegExp }[] = [
  { type: "disavow", pattern: /disavow|toxic (?:link|backlink)|spammy (?:link|backlink)|link spam|bad backlink/i },
  { type: "csv", pattern: /\bcsv\b|bulk (?:recommendation|edit|fix)|recommendations? (?:export|list|spreadsheet)|batch (?:of )?(?:title|meta|page)/i },
  { type: "review", pattern: /review schema|aggregate ?rating|star rating markup|rating schema/i },
  { type: "local", pattern: /\bnap\b|citation|\bgbp\b|google business|business profile|local listing|localbusiness|local pack|opening hours|geo ?coordinates|service area/i },
  { type: "llms", pattern: /llms?\.txt|llm file|ai crawler file/i },
  { type: "robots", pattern: /robots\.txt|crawl budget|crawl directive|disallow rule|blocking (?:css|js|crawler)/i },
  { type: "sitemap", pattern: /sitemap/i },
  { type: "canonical", pattern: /canonical|duplicate (?:content|page|url)|rel[=\s]*canonical/i },
  { type: "redirect", pattern: /redirect|\b30[12]\b|broken link|\b404\b|dead link|redirect chain/i },
  { type: "alt", pattern: /alt text|alt attribute|image alt|missing alt/i },
  { type: "brief", pattern: /content brief|topical? cluster|pillar (?:page|content)|content outline|topic cluster|content gap/i },
];

/** Classic detectors — only consulted after the MCP fixType, as a keyword fallback. */
const CLASSIC_RULES: { type: IssueFixType; pattern: RegExp }[] = [
  { type: "faq", pattern: /\bfaq\b|faqpage|frequently asked/i },
  { type: "entity", pattern: /wikidata|same ?as|entity|organization schema|person schema|knowledge graph/i },
  { type: "meta", pattern: /meta description/i },
  { type: "title", pattern: /title tag|page title|seo title/i },
  { type: "h1", pattern: /\bh1\b|heading structure|main heading/i },
  { type: "schema", pattern: /schema|json-?ld|structured data|rich result|markup/i },
  { type: "content", pattern: /thin content|content depth|word count|expand (?:the )?content/i },
  { type: "competitor", pattern: /competitor|keyword gap|competitive gap/i },
];

/**
 * Resolves the richest applicable fix type for an issue:
 * remediationHint (A44) wins, then card-specific text detectors, then MCP fixType, then classic detectors.
 */
export function resolveFixType(issue: IssueLike): IssueFixType {
  const hint = (issue.remediationHint || "").trim().toLowerCase();
  if (hint && HINT_TO_FIX_TYPE[hint]) return HINT_TO_FIX_TYPE[hint];
  if (hint && ALL_FIX_TYPES.has(hint)) return hint as IssueFixType;

  const hay = `${issue.title || ""} ${issue.description || ""}`;

  for (const rule of CARD_SPECIFIC_RULES) {
    if (rule.pattern.test(hay)) return rule.type;
  }

  const raw = (issue.fixType || "").toLowerCase();
  if (ALL_FIX_TYPES.has(raw)) return raw as IssueFixType;

  for (const rule of CLASSIC_RULES) {
    if (rule.pattern.test(hay)) return rule.type;
  }

  return "technical";
}

export interface ArtifactFile {
  filename: string;
  mime: string;
  /** True for file-oriented outputs whose Download button should be emphasized. */
  emphasize: boolean;
}

interface ExtSpec { ext: string; mime: string; emphasize: boolean }

const EXT_BY_TYPE: Record<IssueFixType, ExtSpec> = {
  schema: { ext: "json", mime: "application/json", emphasize: true },
  faq: { ext: "json", mime: "application/json", emphasize: true },
  review: { ext: "json", mime: "application/json", emphasize: true },
  entity: { ext: "json", mime: "application/json", emphasize: true },
  csv: { ext: "csv", mime: "text/csv", emphasize: true },
  disavow: { ext: "txt", mime: "text/plain", emphasize: true },
  robots: { ext: "txt", mime: "text/plain", emphasize: true },
  llms: { ext: "txt", mime: "text/plain", emphasize: true },
  sitemap: { ext: "xml", mime: "application/xml", emphasize: true },
  redirect: { ext: "txt", mime: "text/plain", emphasize: true },
  brief: { ext: "md", mime: "text/markdown", emphasize: false },
  content: { ext: "md", mime: "text/markdown", emphasize: false },
  competitor: { ext: "md", mime: "text/markdown", emphasize: false },
  meta: { ext: "html", mime: "text/html", emphasize: false },
  title: { ext: "html", mime: "text/html", emphasize: false },
  h1: { ext: "html", mime: "text/html", emphasize: false },
  canonical: { ext: "html", mime: "text/html", emphasize: false },
  alt: { ext: "txt", mime: "text/plain", emphasize: false },
  technical: { ext: "txt", mime: "text/plain", emphasize: false },
};

/** Fixed, well-known filenames (uploaded to the site root or a specific tool). */
const FIXED_NAME: Partial<Record<IssueFixType, string>> = {
  robots: "robots.txt",
  llms: "llms.txt",
  sitemap: "sitemap.xml",
  disavow: "disavow.txt",
};

function slugify(input?: string): string {
  return (input || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40);
}

/**
 * Produces the download filename + MIME type for a generated fix. The code language (from the
 * fenced block) can refine the extension (e.g. a "technical" fix emitted as JSON downloads as
 * .json).
 */
export function resolveArtifactFile(fixType: IssueFixType, language?: string, title?: string): ArtifactFile {
  const spec = EXT_BY_TYPE[fixType] ?? { ext: "txt", mime: "text/plain", emphasize: false };

  const fixed = FIXED_NAME[fixType];
  if (fixed) return { filename: fixed, mime: spec.mime, emphasize: spec.emphasize };

  let { ext, mime } = spec;
  let emphasize = spec.emphasize;
  const lang = (language || "").toLowerCase();
  if (lang === "json") { ext = "json"; mime = "application/json"; }
  else if (lang === "csv") { ext = "csv"; mime = "text/csv"; emphasize = true; }
  else if (lang === "xml") { ext = "xml"; mime = "application/xml"; }

  const slug = slugify(title) || fixType;
  return { filename: `${slug}.${ext}`, mime, emphasize };
}

/** Triggers a browser download of `content` as `filename` with the given MIME type. */
export function downloadArtifact(content: string, filename: string, mime = "text/plain"): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on next tick so the download has a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
